/**
 * Created by Simon Zhao on 7/20/21.
 * Description:
 * The dialog component for MDL modification.
 * ------ maintenance history ------
 */

import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { DialogService } from '@progress/kendo-angular-dialog';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { businessConstants } from '../../constants/business.constants';
import { CreateOrUpdate, MDLTemplateActionTypes } from '../../redux/actions/mdl-template.actions';
import { GetEntityFields, GetTemplateFields } from '../../redux/actions/template-web.actions';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';
import { singleMDLTemplateSelector } from '../../redux/reducers/mdl-template.reducer';
import { singleTemplateFieldsByIdSelector } from '../../redux/reducers/template-fields.reducer';
import { TemplateField } from '../../tamalelibs/models/template-field.model';
import { IFieldMDLProvider, IMDLTemplate, MDLMetaEnum, MDLSourceEnum } from '../../tamalelibs/models/template-mdl.model';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { ToastService } from '../../widgets/toast/toast.service';
import { MdlDialogFieldSectionComponent } from './mdl-dialog-field-section/mdl-dialog-field-section.component';
import { MdlDialogOpenOptions } from './mdl-dialog.model';
import { MdlDialogService } from './mdl-dialog.service';

@Component({
    selector: 'mdl-dialog',
    templateUrl: './mdl-dialog.component.html',
    styleUrls: ['./mdl-dialog.component.scss'],
    providers: [DialogService]
})
export class MdlDialogComponent implements OnInit, OnDestroy {

    isLoading: boolean;

    currentTypeId: string;

    dialogWidth = 500;

    // the list of fields which have configuration already.
    fieldsInUse: string[] = [];

    isProgressing = false;

    // the field config collection.
    mdlSections: UntypedFormArray = new UntypedFormArray([]);

    opened: boolean;

    titleName: string;

    // a flag indicating whether the saving button is disabled.
    get isSaveBtnDisabled(): boolean {
        return !this.mdlSections || this.mdlSections.invalid || this.mdlSections.pristine;
    }

    private _currentTypeName: string;

    private _suscriptions: Subscription[] = [];

    constructor(private _mdlDialogService: MdlDialogService,
        private _toast: ToastService,
        private _store: Store<any>,
        private _alertWindow: AlertWindowService) {
    }

    /**
 * Convert the configs retrieved from server into form group format for edit.
 * @param template the configuration in json formation retrieved from server
 * @param fieldArray the array of fields of current template.
 * @returns current configuration in FormGroup format.
 */
    private static _fromMDLTemplate(template: IMDLTemplate, fieldArray: TemplateField[]): Array<UntypedFormGroup> {
        const secArray: Array<UntypedFormGroup> = [];
        template.mdlTemplate.forEach((value, key) => {
            const fieldIndex = fieldArray.findIndex(f => f.controlID === key);
            const section = new UntypedFormGroup(
                {
                    controlId: new UntypedFormControl(key),
                    controlLabel: new UntypedFormControl(fieldIndex >= 0 ? fieldArray[fieldIndex].label : ''),
                    fieldname: new UntypedFormControl(value.mdlProvider.fieldname, Validators.required),
                    mdlsource: new UntypedFormControl(value.mdlProvider.mdlsource),
                    mdlfieldname: new UntypedFormControl(value.mdlProvider.mdlfieldname, Validators.required),
                    providername: new UntypedFormControl(value.mdlProvider.providername, Validators.required)
                }
            );

            secArray.push(section);
        });

        return secArray;
    }

    /**
     * Convert the config result from form array to IMDLTemplate(aka the json format for saving)
     * @param sections the field sections for current entity/entry type
     * @returns the json format for saving
     */
    private static _toMDLTemplate(sections: UntypedFormArray): IMDLTemplate {
        const sectionMap = new Map<string, IFieldMDLProvider>();

        sections.controls.forEach(
            (sec: UntypedFormGroup) => {
                const provider = {
                    mdlProvider: {
                        fieldname: sec.get(MDLMetaEnum.fieldname).value,
                        mdlfieldname: sec.get(MDLMetaEnum.mdlfieldname).value,
                        providername: sec.get(MDLMetaEnum.providername).value
                    }
                };

                if (sec.contains(MDLMetaEnum.mdlsource) && sec.controls[MDLMetaEnum.mdlsource].value) {
                    provider.mdlProvider[MDLMetaEnum.mdlsource] = sec.get(MDLMetaEnum.mdlsource).value;
                }

                sectionMap.set(sec.get(MDLMetaEnum.controlId).value, provider);
            }
        );

        return { mdlTemplate: sectionMap };
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeunloadHandler($event: any) {
        if (this.opened) {
            $event.returnValue = true;
        }
    }

    getSectionTitle(fg: UntypedFormGroup) {
        return MdlDialogFieldSectionComponent.getCtrlLabel(fg);
    }

    ngOnInit() {
        this._suscriptions.push(this._mdlDialogService.dialogOpen$.pipe(
            filter(action => !this.opened),
        ).subscribe((dialogOpenOptions: MdlDialogOpenOptions) => {
            this.isLoading = true;
            if (dialogOpenOptions.templateItem.name) {
                this.titleName = dialogOpenOptions.templateItem.name + ' Configuration';
            }
            this.currentTypeId = dialogOpenOptions.templateItem.id;
            this._currentTypeName = dialogOpenOptions.templateItem.name;
            this._store.dispatch(new GetTemplateFields(dialogOpenOptions.templateItem.id));
            this._store.dispatch(new GetEntityFields(dialogOpenOptions.templateItem.id));

            this._suscriptions.push(this._store.pipe(
                select(singleMDLTemplateSelector, { typeId: this.currentTypeId })
            ).subscribe(tR => {
                this._suscriptions.push(
                    this._store.pipe(
                        select(singleTemplateFieldsByIdSelector, { id: this.currentTypeId })
                        , filter(fResult => !!fResult)
                    ).subscribe(
                        fR => {
                            const templateResult = tR;
                            const fieldsResult = fR;
                            if (templateResult && fieldsResult) {
                                if (templateResult) {
                                    MdlDialogComponent._fromMDLTemplate(templateResult, fieldsResult).forEach(
                                        ctrl => {
                                            this._pushSection(ctrl);
                                        });
                                }
                            }

                            this.isLoading = false;
                        }
                    ));
            }));
            this.opened = true;
        }));

        this._suscriptions.push(this._store.pipe(
            select(lastCustomActionSelector)
        ).subscribe((action: { type: string }) => {
            this._onActionMessageArrived(action);
        }));
    }

    ngOnDestroy() {
        this._suscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onFieldSelcted(selectedField: TemplateField) {
        this._pushSection(this._createSection4Field(selectedField));
        this.mdlSections.markAsDirty({ onlySelf: true });
    }

    onCancelBtnClick($event: Event) {
        if ($event.stopPropagation) {
            $event.stopPropagation();
        }

        if (!this.mdlSections.pristine) {
            const subscription = this._alertWindow.warn(businessConstants.alertWindow.warnTitle, [businessConstants.alertWindow.warnContent])
                .subscribe((result: boolean) => {
                    if (result) {
                        this._close();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._close();
        }
    }

    onClickSaveTemplate(event: Event) {
        this.isProgressing = true;
        if (event) {
            event.stopPropagation();
        }

        this._store.dispatch(new CreateOrUpdate(
            MdlDialogComponent._toMDLTemplate(this.mdlSections), this.currentTypeId, this._currentTypeName
        ));

        this._close();
    }

    /**
     * The event handler for the section deletion.
     * @param deletedFrm the section to be deleted
     */
    onSectionDeleting(deletedFrm: UntypedFormGroup) {
        const index = Array.from<UntypedFormGroup>(this.mdlSections.value).findIndex(frm => frm[MDLMetaEnum.controlId] === deletedFrm.controls[MDLMetaEnum.controlId].value);
        if (index >= 0) {
            this.mdlSections.removeAt(index);
            this._syncFieldsInUse();
            this.mdlSections.markAsDirty({ onlySelf: true });
        }
    }

    private _close() {
        this.opened = false;
        this.mdlSections.reset();
        this.mdlSections.clear();
        this.currentTypeId = '';
        this.fieldsInUse = [];
    }

    private _createSection4Field(field: TemplateField): UntypedFormGroup {
        const fieldSection = new UntypedFormGroup(
            {
                controlId: new UntypedFormControl(field.controlID),
                controlLabel: new UntypedFormControl(field.label),
                fieldname: new UntypedFormControl('', Validators.required),
                mdlsource: new UntypedFormControl(MDLSourceEnum.web),
                mdlfieldname: new UntypedFormControl('', Validators.required),
                providername: new UntypedFormControl('', Validators.required)
            }
        );

        return fieldSection;
    }

    private _onActionMessageArrived(action: { type: string, payload?: any }) {
        switch (action.type) {
            case MDLTemplateActionTypes.CREATE_OR_UPDATE_SUCCESS:
                this._toast.notify({
                    message: action.payload.typeName + businessConstants.notification.mdlConfig.successfulMsg,
                    style: NotificationStyles.Success
                });
                this.isProgressing = false;
                break;
            case MDLTemplateActionTypes.CREATE_OR_UPDATE_FAILED:
                this._toast.notify({
                    message: action.payload.errorMsg,
                    style: NotificationStyles.Error
                });
                this.isProgressing = false;
                break;
        }
    }

    private _pushSection(sec: UntypedFormGroup) {
        this.mdlSections.push(sec);
        this._syncFieldsInUse();
    }

    private _syncFieldsInUse() {
        this.fieldsInUse = [];
        this.mdlSections.controls.forEach((sec: UntypedFormGroup) => {
            if (sec) {
                this.fieldsInUse.push(sec.controls[MDLMetaEnum.controlId].value);
            }
        });
    }

}
